/**
 * Component Definitions Registry
 *
 * Central registry for all page builder component definitions.
 * Each component is defined with:
 * - type: Unique identifier
 * - label: Display name
 * - icon: Icon name or component
 * - category: 'layout' | 'content' | 'form' | 'advanced'
 * - defaultProps: Default property values
 * - settingsSchema: Array of field configurations for the properties panel
 * - render: Function to render the component
 *
 * Usage:
 *   import { getComponentDefinition, getAllComponentDefinitions } from './componentDefinitions';
 *
 *   const textDef = getComponentDefinition('text');
 *   const allDefs = getAllComponentDefinitions();
 */

// Import component definitions
import { rowComponent } from './layout/row';
import { columnComponent } from './layout/column';
import { containerComponent } from './layout/container';
import { blockComponent } from './layout/block';
import { spacerComponent } from './layout/spacer';
import { textComponent } from './content/text';
import { imageComponent } from './content/image';
import { buttonComponent } from './content/button';
import { iconComponent } from './content/icon';
import { videoComponent } from './content/video';
import { listItemComponent } from './content/listItem';
import { accordionComponent } from './content/accordion';
import { linkComponent } from './content/link';

// Import section definitions
import {
  headerSection,
  paragraphSection,
  imageSection as imageSectionDef,
  videoSection as videoSectionDef,
  ctaSection,
  getSectionDefinition,
  getAllSectionDefinitions,
  hasSection,
  getSectionSettingsSchema,
  getSectionDefaultContent,
} from './sections';

/**
 * Component registry map
 * @type {Map<string, ComponentDefinition>}
 */
const componentRegistry = new Map();

/**
 * Register all built-in components
 */
const builtInComponents = [
  // Layout components
  rowComponent,
  columnComponent,
  containerComponent,
  blockComponent,
  spacerComponent,
  // Content components
  textComponent,
  imageComponent,
  buttonComponent,
  iconComponent,
  videoComponent,
  listItemComponent,
  accordionComponent,
  linkComponent,
];

builtInComponents.forEach((comp) => {
  if (comp) {
    componentRegistry.set(comp.type, comp);
  }
});

/**
 * Get a component definition by type
 * @param {string} type - Component type identifier
 * @returns {ComponentDefinition|undefined}
 */
export function getComponentDefinition(type) {
  return componentRegistry.get(type);
}

/**
 * Get all registered component definitions
 * @returns {ComponentDefinition[]}
 */
export function getAllComponentDefinitions() {
  return Array.from(componentRegistry.values());
}

/**
 * Get components filtered by category
 * @param {string} category - Category to filter by
 * @returns {ComponentDefinition[]}
 */
export function getComponentsByCategory(category) {
  return getAllComponentDefinitions().filter((c) => c.category === category);
}

/**
 * Register a custom component definition
 * @param {ComponentDefinition} definition - Component definition to register
 */
export function registerComponent(definition) {
  if (!definition.type) {
    throw new Error('Component definition must have a type');
  }
  componentRegistry.set(definition.type, definition);
}

/**
 * Check if a component type is registered
 * @param {string} type - Component type to check
 * @returns {boolean}
 */
export function hasComponent(type) {
  return componentRegistry.has(type);
}

/**
 * Get settings schema for a component type
 * @param {string} type - Component type
 * @returns {SettingsField[]|null}
 */
export function getSettingsSchema(type) {
  const def = getComponentDefinition(type);
  return def?.settingsSchema || null;
}

/**
 * Get default props for a component type
 * @param {string} type - Component type
 * @returns {Object|null}
 */
export function getDefaultProps(type) {
  const def = getComponentDefinition(type);
  return def?.defaultProps || null;
}

/**
 * Category name mapping for QuickPropertiesPanel compatibility
 * Maps component definition categories to section names used in field rendering
 */
const categoryToSection = {
  Content: 'Content',
  Typography: 'Typography',
  Layout: 'Layout & Dimensions',
  Size: 'Layout & Dimensions',
  Spacing: 'Spacing',
  Appearance: 'Style & Appearance',
  Border: 'Style & Appearance',
  Behavior: 'Behavior',
  Playback: 'Behavior',
  'Header Style': 'Header Style',
  'Content Style': 'Content Style',
  Position: 'Position',
};

/**
 * Field type mapping for QuickPropertiesPanel compatibility
 * Maps component definition field types to types expected by field rendering
 */
const typeMapping = {
  padding: 'paddingFull',
  margin: 'marginFull',
  dimension: 'dimensionWithUnit',
};

/**
 * Boolean options for checkbox fields converted to select
 */
const booleanSelectOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

/**
 * Get settings for a component in the format expected by QuickPropertiesPanel
 * This adapts component definition settingsSchema to the format used by field rendering
 *
 * @param {Object} component - Component object with type and props
 * @returns {Array} Array of field definitions for QuickPropertiesPanel
 */
export function getSettingsForComponent(component) {
  if (!component) return [];

  const { type, props = {} } = component;
  const definition = getComponentDefinition(type);

  if (!definition || !definition.settingsSchema) {
    return [];
  }

  return definition.settingsSchema.map((field) => {
    // Map category to section
    const section = categoryToSection[field.category] || field.category || 'Other';

    // Map field type if needed
    let mappedType = typeMapping[field.type] || field.type;
    let fieldOptions = field.options;

    // Convert checkbox to select with Yes/No options
    if (field.type === 'checkbox') {
      mappedType = 'select';
      fieldOptions = booleanSelectOptions;
    }

    // Determine default value - use current prop value if available, otherwise use field default
    let defaultValue = field.default;

    if (props[field.key] !== undefined) {
      defaultValue = props[field.key];
    } else if (definition.defaultProps && definition.defaultProps[field.key] !== undefined) {
      defaultValue = definition.defaultProps[field.key];
    }

    return {
      ...field,
      section,
      type: mappedType,
      options: fieldOptions,
      default: defaultValue,
    };
  });
}

// Re-export individual component definitions for direct import
export {
  rowComponent,
  columnComponent,
  containerComponent,
  blockComponent,
  spacerComponent,
  textComponent,
  imageComponent,
  buttonComponent,
  iconComponent,
  videoComponent,
  listItemComponent,
  accordionComponent,
  linkComponent,
};

// Re-export section definitions and utilities
export {
  headerSection,
  paragraphSection,
  imageSectionDef as imageSection,
  videoSectionDef as videoSection,
  ctaSection,
  getSectionDefinition,
  getAllSectionDefinitions,
  hasSection,
  getSectionSettingsSchema,
  getSectionDefaultContent,
};

export default componentRegistry;
